import React, { Component } from 'react';

import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { date } from '@ipfabric/utils';
import { getSnapshotChangesChart } from 'containers/Chartjs';
import get from 'lodash/get';
import PropTypes from 'prop-types';

export default class DeviceChart extends Component {
  static propTypes = {
    dateFormat: PropTypes.string.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    Chart: PropTypes.any.isRequired,
    data: PropTypes.shape({
      discoveryHistory: PropTypes.array.isRequired,
    }).isRequired,
    msg: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
  };

  state = {
    width: null,
    height: null,
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
  }

  shouldComponentUpdate(nextProps, nextState, _nextContext) {
    return (
      nextProps.data.discoveryHistory !== this.props.data.discoveryHistory ||
      this.state.width !== nextState.width ||
      this.state.height !== nextState.height
    );
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    if (
      (prevProps.data.discoveryHistory !== this.props.data.discoveryHistory ||
        this.state.width !== prevState.width ||
        this.state.height !== prevState.height) &&
      this.stackedChart
    ) {
      this.stackedChart.destroy();
      this.stackedChart = null;
      return true;
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {
      data: { discoveryHistory },
    } = this.props;
    const {
      data: { discoveryHistory: prevDiscoveryHistory },
    } = prevProps;
    if (discoveryHistory !== prevDiscoveryHistory) {
      this.drawGraphData();
    } else if (
      this.state.width !== prevState.width ||
      this.state.height !== prevState.height
    ) {
      this.drawGraphData(!(discoveryHistory.length && snapshot));
    }
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.containerRef.current) {
          const { width, height } = entry.contentRect;
          this.handleResize(width, height);
        }
      }
    });

    if (this.containerRef.current) {
      this.resizeObserver.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    if (this.stackedChart) {
      this.stackedChart.destroy();
    }

    if (this.containerRef.current) {
      this.resizeObserver.unobserve(this.containerRef.current);
    }
  }

  drawGraphData = (animationEnabled = true) => {
    const {
      msg,
      Chart,
      data: { discoveryHistory },
      dateFormat,
      dateTimeFormat,
      timezone,
    } = this.props;
    if (discoveryHistory.length === 0) {
      return;
    }
    if (this.deviceChart) {
      const colors = ['rgba(44, 130, 201, 0.6)', '#70AD47', '#FF2616'];
      const hoverColors = ['#4894D5', '#65AD36', '#FF0000'];
      if (discoveryHistory.find((row) => row.tsEnd === undefined)) {
        stumble('Discovery history is missing tsEnd value');
      }
      this.stackedChart = getSnapshotChangesChart(
        Chart,
        this.deviceChart,
        get(msg, ['widgets', 'deviceChart'], {}),
        animationEnabled,
        discoveryHistory.map((row) =>
          row.tsEnd !== undefined
            ? date.formatUtcTimestampInTimeZone(row.tsEnd, timezone, dateFormat)
            : '',
        ),
        ['line', 'bar', 'bar'],
        [
          get(msg, ['widgets', 'deviceChart', 'deviceTotalCount']),
          get(msg, ['widgets', 'deviceChart', 'deviceAddedCount']),
          get(msg, ['widgets', 'deviceChart', 'deviceRemovedCount']),
        ],
        [
          discoveryHistory.map((row) => row.deviceTotalCount ?? 0),
          discoveryHistory.map((row) => row.deviceAddedCount ?? 0),
          discoveryHistory.map((row) => row.deviceRemovedCount ?? 0),
        ],
        colors,
        hoverColors,
        discoveryHistory.map((row) =>
          row.tsEnd !== undefined
            ? date.formatUtcTimestampInTimeZone(
                row.tsEnd,
                timezone,
                dateTimeFormat,
              )
            : '',
        ),
        this.stackedChart,
      );
    }
  };

  handleResize = (width, height) => {
    this.setState({ width, height });
  };

  handleClick = (e) => {
    if (this.stackedChart) {
      const activePoints = this.stackedChart.getElementsAtEvent(e);
      if (activePoints.length > 0) {
        const {
          data: { discoveryHistory },
          navigate,
        } = this.props;
        const index = get(activePoints, [0, '_index']);
        if (index >= 1) {
          navigate(
            `/management/changes/result/devices?before=${get(discoveryHistory, [index - 1, 'snapshot'])}&after=${get(
              discoveryHistory,
              [index, 'snapshot'],
            )}&options=${JSON.stringify({ filters: { or: [{ status: ['eq', 'added'] }, { status: ['eq', 'removed'] }] } })}`,
          );
        }
      }
    }
  };

  render() {
    const {
      msg,
      data: { discoveryHistory },
    } = this.props;
    const { width, height } = this.state;
    if (discoveryHistory.length === 0) {
      return (
        <div>
          <h4 className='dashboard-device-chart__title'>
            {get(msg, 'widgets.deviceChart.title')}
          </h4>
          <p>{get(msg, 'noDataAvailable')}</p>
        </div>
      );
    }
    return (
      <div className='row overflow-x-auto'>
        <div
          className='col-xs-12 dashboard-device-chart__col'
          ref={this.containerRef}
        >
          <canvas
            height={height || 400}
            ref={(c) => {
              this.deviceChart = c;
            }}
            onClick={this.handleClick}
            width={width || 400}
          />
        </div>
      </div>
    );
  }
}
